import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { IOfficerBaseState } from './officer-details-store.state';
import * as OfficerDetailActions from './officer-details.store.actions';
import * as OfficerDetailSelectors from './officer-details-store.selectors';
import { LoggedInOfficer } from '../models/logged-in-officer.models';
import { Observable, distinctUntilChanged, filter, map, pairwise } from 'rxjs';
import { compareValuesLoggedInOfficerUid } from '../functions/comparison.functions';
import { UserRole } from '@shared/utils/base-auth';

@Injectable({
  providedIn: 'root',
})
export class OfficerDetailFacade {
  public constructor(private readonly store: Store<IOfficerBaseState>) {}

  public updateLoggedInOfficer(): void {
    this.store.dispatch(OfficerDetailActions.getCurrentOfficer());
  }

  public getLoggedInOfficer(): Observable<LoggedInOfficer> {
    return this.store.select(OfficerDetailSelectors.loggedInOfficer).pipe(filter((x): x is LoggedInOfficer => !!x));
  }

  public getLoggedInOfficerEmitDistinctUidValue(): Observable<LoggedInOfficer> {
    return this.store.select(OfficerDetailSelectors.loggedInOfficer).pipe(
      filter((x): x is LoggedInOfficer => !!x),
      distinctUntilChanged((previous, current) => compareValuesLoggedInOfficerUid(previous, current))
    );
  }

  public isCurrentOfficerRole(role: UserRole): Observable<boolean> {
    return this.getLoggedInOfficerEmitDistinctUidValue().pipe(map((x) => x.role === role));
  }

  public updateOfficerFilters(newAutoClearFiltersValue: boolean): void {
    this.store.dispatch(OfficerDetailActions.changeAutoClearFilters({ newAutoClearFiltersValue: newAutoClearFiltersValue }));
  }

  public getLoggedInOfficerFilters(): Observable<boolean | undefined> {
    return this.store.select(OfficerDetailSelectors.loggedInOfficerAutoClearFilters);
  }

  public updateLoggedInOfficerDetails(updatedOfficerDetails: LoggedInOfficer): void {
    this.store.dispatch(OfficerDetailActions.updateCurrentOfficerSuccess({ officer: updatedOfficerDetails }));
  }

  public getAutoClearFiltersValueChange(): Observable<boolean> {
    return this.getLoggedInOfficer().pipe(
      map((officer) => !officer.autoClearFilters),
      pairwise(),
      filter(([previous, current]) => !previous && current),
      map(([, current]) => current)
    );
  }
}
